import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSchedule } from '../api';
import { haptic } from '../haptic';
import { toast } from '../toast';

export default function LiveTicker() {
  const nav = useNavigate();
  const { data } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule, refetchInterval: 30_000 });
  const live = useMemo(() => (data?.matches ?? []).filter((m) => m.status === 'in'), [data]);

  // Goal-change detection: compare current scores to prev.
  const prevScores = useRef<Map<string, { h: number; a: number }>>(new Map());
  const [goalFlash, setGoalFlash] = useState<{ id: string; team: string; flag: string; score: string } | null>(null);

  useEffect(() => {
    for (const m of live) {
      const prev = prevScores.current.get(m.id);
      const h = m.home_score ?? 0;
      const a = m.away_score ?? 0;
      if (prev && (h > prev.h || a > prev.a)) {
        const scored = h > prev.h ? 'home' : 'away';
        const team = scored === 'home' ? m.home_name : m.away_name;
        const flag = (scored === 'home' ? m.home_flag : m.away_flag) ?? '⚽';
        setGoalFlash({ id: m.id, team, flag, score: `${h}-${a}` });
        haptic.success();
        toast.success(`GOAL · ${team} ${h}-${a}`);
        setTimeout(() => setGoalFlash(null), 4500);
      }
      prevScores.current.set(m.id, { h, a });
    }
  }, [live]);

  if (live.length === 0) return null;

  return (
    <>
      <div
        className="sticky top-0 z-30 bg-pitch-700/95 backdrop-blur border-b border-pitch-900/50 overflow-hidden"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex items-center gap-3 px-3 py-1.5 overflow-x-auto whitespace-nowrap text-[12px] text-ink-950 font-medium">
          <span className="inline-flex items-center gap-1.5 shrink-0">
            <span className="size-1.5 rounded-full bg-ink-950 animate-pulse" />
            LIVE
          </span>
          {live.map((m) => (
            <button
              key={m.id}
              onClick={() => nav(`/matches/${m.id}`)}
              className="inline-flex items-center gap-1 shrink-0 hover:underline"
            >
              <span>{m.home_flag ?? '🏳️'}</span>
              <span className="font-semibold">{m.home_name}</span>
              <span className="stat">{m.home_score ?? 0}-{m.away_score ?? 0}</span>
              <span className="font-semibold">{m.away_name}</span>
              <span>{m.away_flag ?? '🏳️'}</span>
              <span className="text-ink-950/70 text-[10px] ml-0.5">{m.status_detail ?? ''}</span>
            </button>
          ))}
        </div>
      </div>

      {goalFlash && <GoalOverlay info={goalFlash} onClick={() => nav(`/matches/${goalFlash.id}`)} />}
    </>
  );
}

function GoalOverlay({ info, onClick }: { info: { team: string; flag: string; score: string }; onClick: () => void }) {
  return (
    <div className="fixed top-0 inset-x-0 z-[70] pointer-events-none" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <button
        onClick={onClick}
        className="pointer-events-auto mx-auto mt-2 sm:max-w-md w-[calc(100%-1rem)] block animate-goal-pop"
      >
        <div className="card px-3 py-2.5 ring-2 ring-pitch-500 bg-pitch-500/15 shadow-glow flex items-center gap-3">
          <span className="text-3xl leading-none">{info.flag}</span>
          <div className="flex-1 text-left">
            <div className="text-[10px] uppercase tracking-widest text-pitch-400 font-bold">GOAL</div>
            <div className="text-sm font-semibold text-chalk">{info.team}</div>
          </div>
          <div className="stat text-2xl text-pitch-400">{info.score}</div>
        </div>
      </button>
    </div>
  );
}
